import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import ChurchFlowLogo from "@/components/ChurchFlowLogo";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Email is required");
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md rounded-3xl border-border shadow-none p-8">
        <div className="flex items-center gap-3 mb-6">
          <ChurchFlowLogo size={40} className="soft-glow rounded-2xl" />
          <div className="font-serif text-2xl">ChurchFlow</div>
        </div>
        {sent ? (
          <div className="space-y-5" data-testid="forgot-sent">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <MailCheck className="w-6 h-6" />
            </div>
            <div>
              <h1 className="font-serif text-3xl tracking-tight">Check your inbox</h1>
              <p className="text-muted-foreground mt-2 leading-relaxed">
                If an account exists for <span className="font-medium text-foreground">{email}</span>, we've sent a password reset link. The link expires in 1 hour.
              </p>
              <p className="text-sm text-muted-foreground mt-3">
                Don't see it? Check spam, or try sending again with the correct email.
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="rounded-xl flex-1" onClick={() => setSent(false)} data-testid="forgot-resend">Send again</Button>
              <Link to="/login" className="flex-1"><Button className="rounded-xl w-full">Back to sign in</Button></Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
            <div>
              <h1 className="font-serif text-3xl tracking-tight">Reset your password</h1>
              <p className="text-muted-foreground mt-2 leading-relaxed text-sm">
                Enter the email on your account and we'll send a secure link to set a new password.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl h-12" placeholder="you@church.org" data-testid="forgot-email-input" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-12 rounded-xl soft-glow" data-testid="forgot-submit-button">
              {submitting ? "Sending…" : "Send reset link"}
            </Button>
            <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="forgot-back-link">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}
